/**
 * HALO - Settings Page
 * User settings and preferences management
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Lock,
  Eye,
  Bell,
  ChevronRight,
  Settings as SettingsIcon,
} from "lucide-react";
import { ProfileSection } from "@/components/settings/ProfileSection";
import { PasswordSection } from "@/components/settings/PasswordSection";
import { PrivacySection } from "@/components/settings/PrivacySection";
import { NotificationsSection } from "@/components/settings/NotificationsSection";
import { UserRow } from "@/lib/supabase";

type SettingsTab = "profile" | "password" | "privacy" | "notifications";

interface SettingsPageProps {
  userId: string;
  organizationId: string;
}

interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: "profile",
    label: "Profile",
    icon: User,
    description: "Manage your personal information",
  },
  {
    id: "password",
    label: "Password",
    icon: Lock,
    description: "Update your account password",
  },
  {
    id: "privacy",
    label: "Privacy",
    icon: Eye,
    description: "Control what data is shared",
  },
  {
    id: "notifications",
    label: "Notifications",
    icon: Bell,
    description: "Manage notification preferences",
  },
];

export default function SettingsPage({ userId }: SettingsPageProps) {
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");

  const handleProfileUpdate = (user: UserRow) => {
    // You can propagate this up to update the app state if needed
    console.log("[SettingsPage] Profile updated:", user);
  };

  const renderActiveSection = () => {
    switch (activeTab) {
      case "profile":
        return <ProfileSection userId={userId} onProfileUpdate={handleProfileUpdate} />;
      case "password":
        return <PasswordSection />;
      case "privacy":
        return <PrivacySection userId={userId} />;
      case "notifications":
        return <NotificationsSection userId={userId} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-glass bg-surface-dark/50 backdrop-blur-sm">
        <div className="max-w-6xl mr-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Settings</h1>
              <p className="text-sm text-muted-slate">
                Manage your account and preferences
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mr-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-4 xl:col-span-3">
            <nav className="space-y-2 sticky top-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;

                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all ${
                      isActive
                        ? "bg-primary/10 border-2 border-primary/10"
                        : "bg-surface-elevated border border-glass hover:border-[var(--muted-slate)]"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                        isActive
                          ? "bg-primary/20 text-primary"
                          : "bg-surface-dark text-muted-slate"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`font-semibold ${
                          isActive ? "text-foreground" : "text-muted-slate"
                        }`}
                      >
                        {tab.label}
                      </h3>
                      <p className="text-xs text-muted-slate truncate">
                        {tab.description}
                      </p>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 shrink-0 transition-transform ${
                        isActive ? "text-primary rotate-90" : "text-muted-slate"
                      }`}
                    />
                  </motion.button>
                );
              })}
            </nav>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-8 xl:col-span-9">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {renderActiveSection()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
